import { useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { fetchDealsListData, saveDealsFromPreFetch, setDealsPending, fetchDealOfTheDayData } from '../../features/dealsList/dealsListSlice';
import { fetchLocationDealsData } from '../../features/locationDeals/locationDealsSlice';
import { fetchHotelById, fetchStaticHotelById } from '../../features/search/searchSlice';
import { selectSecondaryFilter } from '../../features/secondaryFilter/secondaryFilterSlice';
import { getParamFromUrl, isEntryFlow } from '../../utilities/urlResolver';
import {
  selectActiveDestinations,
  selectFilterData,
  selectFiltersLoaded,
  specificCitySelectedSelector,
} from '../../features/mainFilter/selectors';
import { selectSearch } from '../../features/mainFilter/searchSlice';
import { prepareSearchRequest } from '../../features/dealsList/dealsListSlice';
import { setDataV2 } from '../../features/wizard-v2/onboardingSlice';
import { getInitialV2State } from '../../features/wizard-v2/wizardService';
import { setSavePrefetchDirectly } from '../../features/dealsList/prefetchDealsSlice';
import abTesting from '../../services/abTesting';

export default function useFetchDeals() {
  const dispatch = useDispatch()
  const specificCitySelected = useSelector(specificCitySelectedSelector);
  const [selectedDestination] = useSelector(selectActiveDestinations);
  const userSearch = useSelector(state => state.userSearch);
  const search = useSelector(selectSearch);
  const filtersReady = useSelector(selectFiltersLoaded);
  const mainFilter = useSelector(selectFilterData);
  const secondaryFilter = useSelector(selectSecondaryFilter);
  const showResults = useSelector(state => state.app.showResults);
  const prefetchDeals = useSelector(state => state.prefetchDeals);

  const {
    fromwhere,
    what,
    where,
    who,
    budget,
  } = mainFilter.searchEngine;
  const { when } = search;

  useEffect(() => {
    let promise;
    let dayDealPromise;

    if (filtersReady && showResults) {
      // if warmup deals already pending, wait for them.
      if (prefetchDeals.pending) {
        dispatch(setSavePrefetchDirectly(true));
        dispatch(setDealsPending(true));
      } else if (prefetchDeals.deals.length > 0) {
        dispatch(setDealsPending(true));
        setTimeout(() => {
          dispatch(saveDealsFromPreFetch({
            deals: prefetchDeals.deals,
            pagination: prefetchDeals.pagination,
          }))
          dispatch(setDealsPending(false));
        }, 500)
      } else {
        promise = dispatch(fetchDealsListData(specificCitySelected));
      }

      if (abTesting.dealOfTheDay) {
        dayDealPromise = dispatch(fetchDealOfTheDayData());
      }

      if (specificCitySelected) {
        dispatch(fetchLocationDealsData({ selectedDestination }));
      }
    }
    if (filtersReady && !showResults && (isEntryFlow() || isEntryFlow('link-presets-welcome'))) {
      const hfLastFilters = JSON.parse(localStorage.getItem('hfLastFilters'));
      const request = prepareSearchRequest({
        mainFilter: mainFilter.searchEngine,
        secondaryFilter: secondaryFilter.data
      });
      if (!getParamFromUrl('pr_capacity')) {
        if (hfLastFilters && hfLastFilters?.engine?.who?.adult) {
          request.engine.who = hfLastFilters.engine.who;
        } else {
          request.engine.who = {};
        }
      }

      const lsRequest = {...request, date: Date.now() };
      localStorage.setItem('hfLastFilters', JSON.stringify(lsRequest));
      dispatch(setDataV2(getInitialV2State()));
    }

    return () => {
      if (dayDealPromise) {
        dayDealPromise.abort();
      }
      if (promise) {
        promise.abort();
      }
    }
  }, [
    dispatch,
    filtersReady,
    showResults,
    specificCitySelected,
    fromwhere,
    what,
    where,
    when,
    who,
    budget,
    secondaryFilter.data,
    selectedDestination,
    mainFilter.searchEngine,
    prefetchDeals.pending,
  ])

  useEffect(() => {
    if(filtersReady && userSearch.selectedHotel.hotelId) {
      const { hotelId } = userSearch.selectedHotel;

      if (!userSearch.selectedHotel.hfDestinationId && hotelId) {
        dispatch(fetchHotelById({
          id: hotelId,
          destinationIds: getParamFromUrl('destinations') || getParamFromUrl('pr_destinations')
        }));
      }

      if (filtersReady) {
        dispatch(fetchStaticHotelById({ id: hotelId }));
      }
    }
  }, [dispatch, filtersReady])
}
